import { memo, useCallback, useMemo } from 'react';

import { SortDirection } from '@air/api/types';
import { ArrowDown, Check as CheckIcon, Sorting } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { ButtonGroup } from '@air/primitive-button-group';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { DropdownMenuWithActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';
import { getConfigFieldLabel } from '~/constants/SortFieldsMap';

export type SortMenuProps = {
  direction: SortDirection;
  currentSortBy: string;
  onSortChange: (sortBy: string, direction: SortDirection) => void;
  sortOptions: readonly string[];
  showDirectionInMenu?: boolean;
  'data-testid'?: string;
};

export const SortMenu = memo(
  ({
    onSortChange,
    currentSortBy,
    direction,
    sortOptions,
    showDirectionInMenu,
    'data-testid': testId,
  }: SortMenuProps) => {
    const supportsDirection = currentSortBy !== 'customSort';

    const onSortDirectionChange = (newDirection: SortDirection) => onSortChange(currentSortBy, newDirection);

    const getSelectedSuffix = useCallback(() => {
      if (showDirectionInMenu) {
        return (
          <ArrowDown
            className={cn('size-4 text-altitude-10', direction === SortDirection.asc ? 'rotate-180' : 'rotate-0')}
          />
        );
      }

      return <CheckIcon className="size-4" />;
    }, [direction, showDirectionInMenu]);

    const options = useMemo(() => {
      const optionsArray: DropdownMenuOption[] = [
        {
          id: 'sort-by',
          label: 'Sort by',
          type: 'title',
        },
      ];

      sortOptions.map((option) => {
        if (option === 'customSort') {
          optionsArray.push({
            id: 'custom-sort-separator',
            type: 'separator',
          });
        }

        const isSelected = option === currentSortBy;

        return optionsArray.push({
          id: option,
          label: getConfigFieldLabel(option),
          onSelect: () => {
            if (showDirectionInMenu && isSelected) {
              onSortChange(option, direction === SortDirection.desc ? SortDirection.asc : SortDirection.desc);
            } else {
              onSortChange(option, direction);
            }
          },
          suffix: isSelected ? getSelectedSuffix() : null,
          type: 'item',
        });
      });

      return optionsArray;
    }, [currentSortBy, direction, getSelectedSuffix, onSortChange, showDirectionInMenu, sortOptions]);

    const tooltipLabel = useMemo(
      () => `${getConfigFieldLabel(currentSortBy)} ${direction === SortDirection.asc ? '↑' : '↓'}`,
      [currentSortBy, direction],
    );

    return (
      <ButtonGroup>
        {showDirectionInMenu ? (
          <Tooltip label={tooltipLabel}>
            <div>
              <DropdownMenuWithActionSheet
                align="end"
                options={options}
                trigger={
                  <IconButton
                    appearance="ghost"
                    color="grey"
                    data-testid={`${testId}-MENU`}
                    label={tooltipLabel}
                    size="small"
                    icon={Sorting}
                  />
                }
                title="Sort by"
              />
            </div>
          </Tooltip>
        ) : (
          <DropdownMenuWithActionSheet
            align="end"
            options={options}
            trigger={
              <Button appearance="ghost" color="grey" data-testid={`${testId}-MENU`} size="small">
                {getConfigFieldLabel(currentSortBy)}
              </Button>
            }
            title="Sort by"
          />
        )}
        {supportsDirection && !showDirectionInMenu && (
          <Tooltip label="Sort ascending/descending">
            <IconButton
              appearance="ghost"
              color="grey"
              data-testid={`${testId}-DIRECTION`}
              data-sort={currentSortBy}
              label="Sort ascending/descending"
              onClick={() => {
                direction === SortDirection.desc
                  ? onSortDirectionChange(SortDirection.asc)
                  : onSortDirectionChange(SortDirection.desc);
              }}
              size="small"
            >
              <ArrowDown className={cn('size-3', direction === SortDirection.asc ? 'rotate-180' : 'rotate-0')} />
            </IconButton>
          </Tooltip>
        )}
      </ButtonGroup>
    );
  },
);

SortMenu.displayName = 'SortMenu';
